/**
 * useCouponRedemption Hook
 * 管理促核销组件的数据获取、转换、分类、聚合、排序逻辑
 *
 * Requirements 1.1, 1.2:
 * - 用户符合曝光人群条件 AND 账户中有未使用的VIP优惠券或VIP电卡时展示组件
 * - 用户不符合曝光人群条件 OR 账户中没有未使用的VIP卡券时隐藏组件
 */

import { useCallback, useMemo } from 'react';
import {
  DisplayItem,
  MyMemberGoodsResp,
  UseCouponRedemptionReturn,
} from '../types/coupon-redemption';
import { processCardItems } from '../utils/coupon-redemption';

/** Hook 配置选项 */
export interface UseCouponRedemptionOptions {
  /** 曝光人群ID，用于判断是否展示组件 */
  audienceId?: string;
  /** 用户是否符合曝光人群条件，默认 true（由外部传入） */
  isInAudience?: boolean;
  /** 从 context 传入的会员商品数据 */
  myMemberGoods?: MyMemberGoodsResp | null;
  /** 数据是否正在加载 */
  isLoading?: boolean;
  /** 刷新数据的回调函数 */
  onRefresh?: () => void;
}

/**
 * 促核销组件数据管理 Hook
 *
 * 功能：
 * 1. 数据转换：将 API 响应转换为统一的数据结构
 * 2. 分类处理：根据到账时间和过期时间对卡券进行分类
 * 3. 聚合处理：根据阈值和模版对优惠券进行聚合
 * 4. 排序处理：按分类和内部规则进行排序
 * 5. 展示判断：根据用户条件和卡券数量判断是否展示组件
 *
 * @param options Hook 配置选项
 * @returns Hook 返回值
 */
export function useCouponRedemption(
  options: UseCouponRedemptionOptions = {}
): UseCouponRedemptionReturn {
  const { isInAudience = true, myMemberGoods, isLoading = false, onRefresh } = options;

  // 处理后的展示列表
  const displayList = useMemo<DisplayItem[]>(() => {
    try {
      // 无数据时返回空数组
      if (!myMemberGoods) {
        return [];
      }

      const currentTime = Date.now();
      return processCardItems(myMemberGoods, currentTime);
    } catch (error) {
      console.error('[CouponRedemption] 数据处理失败:', error);
      // TODO: 上报到 Sentry
      // Sentry.captureException(error, {
      //   tags: { component: 'CouponRedemption' },
      //   extra: { myMemberGoods },
      // });
      return []; // 返回空数组，组件不展示
    }
  }, [myMemberGoods]);

  // 判断是否应该展示组件
  // Requirements 1.1, 1.2:
  // - 用户符合曝光人群条件 AND 账户中有未使用的VIP优惠券或VIP电卡时展示
  // - 用户不符合曝光人群条件 OR 账户中没有未使用的VIP卡券时隐藏
  const shouldShow = useMemo<boolean>(() => {
    // 条件1：用户必须符合曝光人群条件
    if (!isInAudience) {
      return false;
    }

    // 条件2：账户中必须有未使用的VIP卡券
    if (displayList.length === 0) {
      return false;
    }

    return true;
  }, [isInAudience, displayList.length]);

  // 刷新数据的回调函数
  const refresh = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  return {
    displayList,
    loading: isLoading,
    shouldShow,
    refresh,
  };
}

export default useCouponRedemption;
